import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { User } from '../generated/gql-types';
import { userActions } from './user.action';

interface UserState {
  isLoading: boolean;
  currentUser: User | null;
  impersonatedUserId: string | null;
  userFetchError: string | null;
  retryCount: number;
  setCurrentUser: (user: User | null) => void;
  clearUser: () => void;
  setImpersonatedUser: (impersonateUserId: string) => void;
  setUserFetchError: (error: string | null) => void;
  resetRetryCount: () => void;
  incrementRetryCount: () => void;
  fetchCurrentUser: () => Promise<User | null>;
  fetchCurrentUserOnMobile: (options?: {
    networkOnly?: boolean;
  }) => Promise<User | null>;
}

export const useUserStore = create<UserState>()(
  persist(
    (set, get) => ({
      // States
      isLoading: false,
      currentUser: null,
      impersonatedUserId: null,
      userFetchError: null,
      retryCount: 0,

      // Setters
      setCurrentUser: (currentUser) =>
        set({ currentUser, userFetchError: null, retryCount: 0 }),
      clearUser: () =>
        set({
          currentUser: null,
          userFetchError: null,
          retryCount: 0,
          isLoading: false,
        }),
      setImpersonatedUser: (impersonatedUserId: string) =>
        set({ impersonatedUserId }),
      setUserFetchError: (error: string | null) =>
        set({ userFetchError: error }),
      resetRetryCount: () => set({ retryCount: 0 }),
      incrementRetryCount: () =>
        set((state) => ({ retryCount: state.retryCount + 1 })),

      // Actions
      fetchCurrentUser: async () => {
        try {
          const user = await userActions.fetchCurrentUser();
          set({
            // @ts-ignore
            currentUser: user,
          });
          return user;
        } catch (error) {
          set({ currentUser: null });
          throw error;
        }
      },
      fetchCurrentUserOnMobile: async (options?: { networkOnly?: boolean }) => {
        try {
          set({ isLoading: true, userFetchError: null });
          const user = await userActions.fetchCurrentUserOnMobile(options);
          if (user) {
            set({
              currentUser: user,
              isLoading: false,
              userFetchError: null,
              retryCount: 0,
            });
          } else {
            set({
              isLoading: false,
              userFetchError: 'Failed to fetch user data',
            });
          }
          return user;
        } catch (error) {
          console.error('Error fetching current user:', error);
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Failed to fetch user data';
          set({ isLoading: false, userFetchError: errorMessage });
          throw error;
        }
      },
    }),
    {
      name: 'user-storage',
      storage: createJSONStorage(() => AsyncStorage),
      partialize: (state) => ({ currentUser: state.currentUser }),
    },
  ),
);

import { setUserStoreReader } from './userStoreRef';

setUserStoreReader(() => useUserStore.getState());
